import { Control } from 'react-hook-form'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@w/components/ui/form'
import { Input } from '@w/components/ui/input'
import { Textarea } from '@w/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@w/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@w/components/ui/radio-group'
import { Checkbox } from '@w/components/ui/checkbox'
import { Label } from '@w/components/ui/label'

export interface CustomQuestion {
  id: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'select' | 'radio' | 'checkbox'
  options?: string[]
  required?: boolean
  placeholder?: string
  help_text?: string
  validation?: {
    min_length?: number
    max_length?: number
    min?: number
    max?: number
  }
  display_order?: number
  show_on_step?: 'service' | 'contact'
}

interface CustomQuestionsProps {
  questions: CustomQuestion[]
  control: Control<any>
  step: 'service' | 'contact'
}

export default function CustomQuestions({ questions, control, step }: CustomQuestionsProps) {
  // Filter questions for this step and sort by display_order
  const filteredQuestions = questions
    .filter((q) => q.show_on_step === step)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))

  if (filteredQuestions.length === 0) {
    return null
  }

  const renderQuestion = (question: CustomQuestion) => {
    const fieldName = `customFields.${question.id}`

    switch (question.type) {
      case 'text':
        return (
          <FormField
            key={question.id}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {question.label}
                  {question.required && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={question.placeholder || ''}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                {question.help_text && (
                  <p className="text-xs text-muted-foreground">{question.help_text}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'number':
        return (
          <FormField
            key={question.id}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {question.label}
                  {question.required && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder={question.placeholder || ''}
                    className="h-11"
                    min={question.validation?.min}
                    max={question.validation?.max}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber || e.target.value)}
                  />
                </FormControl>
                {question.help_text && (
                  <p className="text-xs text-muted-foreground">{question.help_text}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'textarea':
        return (
          <FormField
            key={question.id}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {question.label}
                  {question.required && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={question.placeholder || ''}
                    rows={3}
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                {question.help_text && (
                  <p className="text-xs text-muted-foreground">{question.help_text}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'select':
        return (
          <FormField
            key={question.id}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {question.label}
                  {question.required && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={question.placeholder || 'Select an option'} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {question.options?.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {question.help_text && (
                  <p className="text-xs text-muted-foreground">{question.help_text}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'radio':
        return (
          <FormField
            key={question.id}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {question.label}
                  {question.required && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} defaultValue={field.value}>
                    {question.options?.map((option) => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`${question.id}-${option}`} />
                        <Label htmlFor={`${question.id}-${option}`}>{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                {question.help_text && (
                  <p className="text-xs text-muted-foreground">{question.help_text}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'checkbox':
        return (
          <FormField
            key={question.id}
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
                </FormControl>
                <div className="space-y-1 leading-none flex-1">
                  <FormLabel className="font-normal cursor-pointer">
                    {question.label}
                    {question.required && <span className="text-destructive ml-1">*</span>}
                  </FormLabel>
                  {question.help_text && (
                    <p className="text-xs text-muted-foreground">{question.help_text}</p>
                  )}
                </div>
              </FormItem>
            )}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {filteredQuestions.map((question) => renderQuestion(question))}
    </div>
  )
}
